import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Loader2, AlertCircle, Search, Shield, ShieldOff, Plus, Minus,
  CreditCard, Gauge, Database, Edit, Trash2, UserPlus, Power, UserCog, Receipt
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(0);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [newForm, setNewForm] = useState({ email: '', company_name: '', password: '', phone: '', credits: 0, is_admin: false });
  const [saving, setSaving] = useState(false);
  const [fiscalUser, setFiscalUser] = useState(null);
  const [fiscalForm, setFiscalForm] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data.users || []);
    } catch { toast.error('Error al cargar usuarios'); }
    finally { setLoading(false); }
  };

  const filteredUsers = users.filter(u =>
    (u.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const toggleAdmin = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/admin?is_admin=${!user.is_admin}`, {}, { headers });
      toast.success('Estado de admin actualizado');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleFeature = async (userId, feature) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const current = user.features || { flex_decrypt: false, slave_manager: false };
    const updated = { ...current, [feature]: !current[feature] };
    try {
      await axios.put(`${API}/admin/user-features/${userId}`, { features: updated }, { headers });
      toast.success(`${feature === 'flex_decrypt' ? 'FLEX Decrypt' : 'Gestor SLAVE'} ${updated[feature] ? 'activado' : 'desactivado'}`);
      fetchUsers();
    } catch { toast.error('Error al actualizar permisos'); }
  };

  const toggleActive = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { active: !(user.active !== false) }, { headers });
      toast.success(user.active !== false ? 'Usuario desactivado' : 'Usuario activado');
      fetchUsers();
    } catch { toast.error('Error'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('¿Seguro que quieres eliminar este usuario? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const adjustCredits = async () => {
    if (!selectedUser || creditsToAdd === 0) return;
    try {
      await axios.post(`${API}/admin/users/${selectedUser.id}/credits`, { amount: creditsToAdd }, { headers });
      toast.success(`Créditos ajustados: ${creditsToAdd > 0 ? '+' : ''}${creditsToAdd}`);
      setSelectedUser(null); setCreditsToAdd(0);
      fetchUsers();
    } catch { toast.error('Error al ajustar créditos'); }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({ company_name: user.company_name || '', email: user.email, phone: user.phone || '' });
  };

  const openFiscal = (user) => {
    setFiscalUser(user);
    const fd = user.fiscal_data || {};
    setFiscalForm({
      fiscal_name: fd.fiscal_name || '',
      cif: fd.cif || fd.fiscal_id || '',
      fiscal_address: fd.fiscal_address || '',
      fiscal_city: fd.fiscal_city || '',
      fiscal_postal_code: fd.fiscal_postal_code || fd.fiscal_zip || '',
      fiscal_province: fd.fiscal_province || '',
      fiscal_country: fd.fiscal_country || 'España',
    });
  };

  const saveFiscal = async () => {
    if (!fiscalUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${fiscalUser.id}`, { fiscal_data: fiscalForm }, { headers });
      toast.success('Datos de facturación actualizados');
      setFiscalUser(null);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${editUser.id}`, editForm, { headers });
      toast.success('Usuario actualizado');
      setEditUser(null);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const createUser = async () => {
    if (!newForm.email || !newForm.company_name || !newForm.password) return toast.error('Email, empresa y contraseña son obligatorios');
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users`, newForm, { headers });
      toast.success('Usuario creado');
      setNewUserDialog(false);
      setNewForm({ email: '', company_name: '', password: '', phone: '', credits: 0, is_admin: false });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-users-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">USUARIOS</h1>
            <p className="text-zinc-400 mt-1">{users.length} usuarios registrados</p>
          </div>
          <Button onClick={() => setNewUserDialog(true)} className="bg-primary hover:bg-primary/90" data-testid="create-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />Nuevo Usuario
          </Button>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por empresa o email..."
            className="pl-10 bg-surface border-white/10 text-white" data-testid="users-search" />
        </div>

        <div className="bg-surface border border-white/10 rounded-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" /></div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center"><AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" /><p className="text-zinc-400">No hay usuarios</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-surface-highlight">
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider">Empresa</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider">Email</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider">Créditos</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider">Rol</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider">Permisos</th>
                    <th className="text-left p-4 text-xs text-zinc-500 uppercase tracking-wider">Estado</th>
                    <th className="text-right p-4 text-xs text-zinc-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={`border-b border-white/5 ${user.active === false ? 'opacity-50' : ''}`}>
                      <td className="p-4">
                        <p className="text-white font-medium">{user.company_name}</p>
                        <p className="text-zinc-600 text-xs">{user.phone || ''}</p>
                      </td>
                      <td className="p-4 text-zinc-400 text-sm">{user.email}</td>
                      <td className="p-4"><span className="font-mono text-lg text-primary">{user.credits}</span></td>
                      <td className="p-4">
                        {user.is_admin ? (
                          <span className="inline-flex items-center gap-1 text-green-500 text-sm"><Shield className="w-3.5 h-3.5" />Admin</span>
                        ) : (
                          <span className="text-zinc-500 text-sm">Usuario</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => toggleFeature(user.id, 'flex_decrypt')}
                            className={`px-2 py-1 rounded text-[10px] font-mono font-bold transition-colors ${user.features?.flex_decrypt ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-zinc-800 text-zinc-600 border border-white/5 hover:text-zinc-400'}`}
                            data-testid={`toggle-flex-${user.id}`}>
                            <Gauge className="w-3 h-3 inline mr-1" />FLEX
                          </button>
                          <button onClick={() => toggleFeature(user.id, 'slave_manager')}
                            className={`px-2 py-1 rounded text-[10px] font-mono font-bold transition-colors ${user.features?.slave_manager ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-zinc-800 text-zinc-600 border border-white/5 hover:text-zinc-400'}`}
                            data-testid={`toggle-slave-${user.id}`}>
                            <Database className="w-3 h-3 inline mr-1" />SLAVE
                          </button>
                        </div>
                      </td>
                      <td className="p-4">
                        <button onClick={() => toggleActive(user.id)}
                          className={`text-xs px-2 py-0.5 rounded-full cursor-pointer ${user.active !== false ? 'text-green-400 bg-green-500/10 hover:bg-green-500/20' : 'text-red-400 bg-red-500/10 hover:bg-red-500/20'}`}
                          data-testid={`toggle-active-${user.id}`}>
                          {user.active !== false ? 'Activo' : 'Inactivo'}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white" onClick={() => openEdit(user)} title="Editar">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-primary hover:text-primary/80" onClick={() => setSelectedUser(user)} title="Créditos">
                            <CreditCard className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300" onClick={() => openFiscal(user)} title="Facturación">
                            <Receipt className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className={user.is_admin ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}
                            onClick={() => toggleAdmin(user.id)} title={user.is_admin ? 'Quitar admin' : 'Hacer admin'}>
                            {user.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => deleteUser(user.id)} title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Credits Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setCreditsToAdd(0); }}>
        <DialogContent aria-describedby={undefined} className="bg-surface border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-xl">AJUSTAR CRÉDITOS</DialogTitle></DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="bg-surface-highlight rounded-md p-4 text-center">
                <p className="text-zinc-400 text-sm">{selectedUser.company_name}</p>
                <p className="font-mono text-3xl text-primary mt-2">{selectedUser.credits}</p>
                <p className="text-zinc-500 text-sm">créditos actuales</p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="icon" className="border-white/10 text-white" onClick={() => setCreditsToAdd(creditsToAdd - 10)}>
                  <Minus className="w-4 h-4" />
                </Button>
                <Input type="number" value={creditsToAdd} onChange={(e) => setCreditsToAdd(parseInt(e.target.value) || 0)}
                  className="bg-surface-highlight border-white/10 text-white text-center" />
                <Button variant="outline" size="icon" className="border-white/10 text-white" onClick={() => setCreditsToAdd(creditsToAdd + 10)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-center text-sm">
                <span className="text-zinc-400">Nuevo saldo: </span>
                <span className={`font-mono font-bold ${selectedUser.credits + creditsToAdd < 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {selectedUser.credits + creditsToAdd}
                </span>
              </div>
              <Button onClick={adjustCredits} disabled={creditsToAdd === 0} className="w-full bg-primary hover:bg-primary/90">
                {creditsToAdd >= 0 ? <><Plus className="w-4 h-4 mr-2" />Añadir {creditsToAdd} créditos</> : <><Minus className="w-4 h-4 mr-2" />Quitar {Math.abs(creditsToAdd)} créditos</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent aria-describedby={undefined} className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><UserCog className="w-5 h-5 text-primary" />EDITAR USUARIO</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 text-sm">Empresa</Label>
              <Input value={editForm.company_name || ''} onChange={e => setEditForm({...editForm, company_name: e.target.value})}
                className="bg-surface-highlight border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">Email</Label>
              <Input value={editForm.email || ''} onChange={e => setEditForm({...editForm, email: e.target.value})}
                className="bg-surface-highlight border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">Teléfono</Label>
              <Input value={editForm.phone || ''} onChange={e => setEditForm({...editForm, phone: e.target.value})}
                className="bg-surface-highlight border-white/10 text-white" />
            </div>
            <Button onClick={saveEdit} disabled={saving} className="w-full bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Guardar Cambios
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={newUserDialog} onOpenChange={setNewUserDialog}>
        <DialogContent aria-describedby={undefined} className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />NUEVO USUARIO</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 text-sm">Email <span className="text-red-400">*</span></Label>
              <Input value={newForm.email} onChange={e => setNewForm({...newForm, email: e.target.value})} placeholder="email@ejemplo.com"
                className="bg-surface-highlight border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">Empresa <span className="text-red-400">*</span></Label>
              <Input value={newForm.company_name} onChange={e => setNewForm({...newForm, company_name: e.target.value})} placeholder="Nombre empresa"
                className="bg-surface-highlight border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">Contraseña <span className="text-red-400">*</span></Label>
              <Input value={newForm.password} onChange={e => setNewForm({...newForm, password: e.target.value})} placeholder="Mínimo 6 caracteres" type="password"
                className="bg-surface-highlight border-white/10 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-sm">Teléfono</Label>
                <Input value={newForm.phone} onChange={e => setNewForm({...newForm, phone: e.target.value})} placeholder="+34..."
                  className="bg-surface-highlight border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Créditos iniciales</Label>
                <Input value={newForm.credits} onChange={e => setNewForm({...newForm, credits: parseInt(e.target.value) || 0})} type="number"
                  className="bg-surface-highlight border-white/10 text-white" />
              </div>
            </div>
            <label className="flex items-center gap-3 p-3 rounded-md border border-white/10 bg-surface-highlight cursor-pointer hover:border-primary/30 transition-colors">
              <input type="checkbox" checked={newForm.is_admin} onChange={e => setNewForm({...newForm, is_admin: e.target.checked})} className="accent-primary w-4 h-4" />
              <Shield className="w-4 h-4 text-green-500" />
              <span className="text-zinc-300 text-sm">Crear como Administrador</span>
            </label>
            <Button onClick={createUser} disabled={saving} className="w-full bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}Crear Usuario
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fiscal Data Dialog */}
      <Dialog open={!!fiscalUser} onOpenChange={() => setFiscalUser(null)}>
        <DialogContent aria-describedby={undefined} className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-400" />DATOS DE FACTURACIÓN
            </DialogTitle>
          </DialogHeader>
          {fiscalUser && (
            <div className="space-y-4">
              <div className="bg-surface-highlight rounded-md p-3 mb-2">
                <p className="text-zinc-400 text-xs">Cliente</p>
                <p className="text-white font-medium">{fiscalUser.company_name || fiscalUser.email}</p>
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Razón Social / Nombre fiscal</Label>
                <Input value={fiscalForm.fiscal_name || ''} onChange={e => setFiscalForm({...fiscalForm, fiscal_name: e.target.value})}
                  placeholder="Nombre fiscal completo" className="bg-surface-highlight border-white/10 text-white" data-testid="fiscal-name" />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">CIF / NIF / VAT</Label>
                <Input value={fiscalForm.cif || ''} onChange={e => setFiscalForm({...fiscalForm, cif: e.target.value})}
                  placeholder="B12345678" className="bg-surface-highlight border-white/10 text-white" data-testid="fiscal-cif" />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Dirección fiscal</Label>
                <Input value={fiscalForm.fiscal_address || ''} onChange={e => setFiscalForm({...fiscalForm, fiscal_address: e.target.value})}
                  placeholder="Calle, número, piso" className="bg-surface-highlight border-white/10 text-white" data-testid="fiscal-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-sm">Ciudad</Label>
                  <Input value={fiscalForm.fiscal_city || ''} onChange={e => setFiscalForm({...fiscalForm, fiscal_city: e.target.value})}
                    placeholder="Madrid" className="bg-surface-highlight border-white/10 text-white" data-testid="fiscal-city" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-sm">Código Postal</Label>
                  <Input value={fiscalForm.fiscal_postal_code || ''} onChange={e => setFiscalForm({...fiscalForm, fiscal_postal_code: e.target.value})}
                    placeholder="28001" className="bg-surface-highlight border-white/10 text-white" data-testid="fiscal-postal-code" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-sm">Provincia</Label>
                  <Input value={fiscalForm.fiscal_province || ''} onChange={e => setFiscalForm({...fiscalForm, fiscal_province: e.target.value})}
                    placeholder="Madrid" className="bg-surface-highlight border-white/10 text-white" data-testid="fiscal-province" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-sm">País</Label>
                  <Input value={fiscalForm.fiscal_country || ''} onChange={e => setFiscalForm({...fiscalForm, fiscal_country: e.target.value})}
                    placeholder="España" className="bg-surface-highlight border-white/10 text-white" data-testid="fiscal-country" />
                </div>
              </div>
              <Button onClick={saveFiscal} disabled={saving} className="w-full bg-amber-600 hover:bg-amber-500" data-testid="save-fiscal-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}Guardar Facturación
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminUsersPage;
